import { useRef, useState } from "react";
import api from "../../../../../../API/axiosInstance";

import { CRAWL_MESSAGES, TYPE_MAP } from "../constants";
import { showToast } from "../../../toastUtils";
import { generateHash } from "../../../../../../API/hashUtil";

/**
 * Encapsulates crawl submission + retry logic.
 *
 * @param {Object} params
 * @param {Function} params.scheduleCrawlRefresh - callback to trigger delayed REST re-fetch
 */
export default function useCrawlSubmit({ scheduleCrawlRefresh }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = localStorage.getItem("Token");

  const handleCrawlSubmit = async ({
    overrides = {},
    selectedPlatformss,
    keywordInput,
    postCount,
    crawlType,
    userProfileInput,
    crawlOutputType,
    sortOption,
  }) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const platform = (
        overrides.platform ??
        (Array.isArray(selectedPlatformss)
          ? selectedPlatformss[0]
          : selectedPlatformss) ??
        ""
      ).toLowerCase();

      const keyword = overrides.keyword ?? keywordInput;
      const count = overrides.postCount ?? postCount;

      const arrayResult = keyword
        .split(",")
        .map((w) => w.trim())
        .filter(Boolean);

      const payload = {
        platform,
        postCount: count,
        ...(crawlType === "keyword"
          ? { keywords: arrayResult }
          : { keywords: [userProfileInput] }),
        type: overrides.type ?? TYPE_MAP[crawlOutputType],
        sourceType: crawlType,
        filter:
          crawlType === "user"
            ? "user"
            : (overrides.filter ??
              (sortOption ? sortOption.toLowerCase() : undefined)),
      };

      Object.keys(payload).forEach(
        (k) => payload[k] === undefined && delete payload[k],
      );

      const hexHash = generateHash(payload);

      showToast(
        CRAWL_MESSAGES[platform] ||
          "Crawl initiated — results will appear shortly",
        "success",
      );

      const response = await api.post(`/aws/crawl-data`, payload, {
        params: { HPPcode: hexHash },
        headers: {
          Authorization: `Bearer ${token}`,
          "X-XSS-Protection": "1; mode=block",
          "X-Content-Type-Options": "nosniff",
          "X-Frame-Options": "DENY",
          "Referrer-Policy": "strict-origin-when-cross-origin",
          "Permissions-Policy": "camera=(), microphone=(), geolocation=()",
          "Content-Type": "application/json",
        },
        validateStatus: () => true,
      });

      switch (response.status) {
        case 200:
          scheduleCrawlRefresh();
          break;
        case 204:
          showToast("No results found for the given keywords", "warning");
          scheduleCrawlRefresh();
          break;
        case 429:
          showToast(
            "Rate limit reached — please try again in a moment",
            "error",
          );
          break;
        case 500:
          showToast("Crawl failed — please try again", "error");
          break;
        default:
          showToast(`Something went wrong (${response.status})`, "error");
      }
    } catch (error) {
      console.error("Axios Error:", error);
      showToast("Connection error — check your network", "error");
    } finally {
      setTimeout(() => setIsSubmitting(false), 5000);
    }
  };

  const handleRetryCrawl = async (sessionID) => {
    const hexHash = generateHash({ sessionId: sessionID?.toString() || "" });
    try {
      const response = await api.post(
        `/aws/retry-crawl-data`,
        {},
        {
          params: { sessionId: sessionID, HPPcode: hexHash },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (response.status === 200) {
        showToast("Retry started successfully 🚀♻️", "success");
        scheduleCrawlRefresh();
      }
    } catch (err) {
      console.error("Retry crawl failed:", err);
      showToast("Retry failed ❌", "error");
    }
  };

  return { isSubmitting, handleCrawlSubmit, handleRetryCrawl };
}
